import React, { useState, useEffect } from "react";
import { Card, Button, Table, Modal, Form, Alert } from "react-bootstrap";
import {
  getSliders,
  createSlider,
  updateSlider,
  deleteSlider,
  getUploadUrl,
} from "../../services/api";
import "./SliderManagement.css";
import "./Dashboard.css";

const SliderManagement = () => {
  const FALLBACK_SLIDER_IMAGE =
    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="150" height="80"><rect width="100%" height="100%" fill="%23e9ecef"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%236c757d" font-family="Arial" font-size="14">No Image</text></svg>';

  const [sliders, setSliders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentSlider, setCurrentSlider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [mobileSupported, setMobileSupported] = useState(false);
  const [mobileFile, setMobileFile] = useState(null);
  const [mobilePreview, setMobilePreview] = useState(null);
  const [removeMobile, setRemoveMobile] = useState(false);

  useEffect(() => {
    if (!mobileFile) {
      setMobilePreview(!removeMobile && currentSlider?.foto_mobile
        ? getUploadUrl(currentSlider.foto_mobile) : null);
      return;
    }
    const url = URL.createObjectURL(mobileFile);
    setMobilePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [mobileFile, currentSlider, removeMobile]);

  // Form state
  const [formData, setFormData] = useState({
    judul: "",
    deskripsisingkat: "",
    deskripsi: "",
    foto: null,
    link: "",
    teks_tombol: "",
    judul_en: "",
    deskripsisingkat_en: "",
    deskripsi_en: "",
    teks_tombol_en: "",
  });

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getSliders();
      if (response.data.success) {
        setSliders(response.data.data);
        setMobileSupported(response.data.mobile_upload_supported === true);
      }
    } catch (err) {
      setError("Gagal memuat data slider");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (slider = null) => {
    setMobileFile(null);
    setRemoveMobile(false);
    setError(null);
    if (slider) {
      setCurrentSlider(slider);
      setFormData({
        judul: slider.judul || "",
        deskripsisingkat: slider.deskripsisingkat || "",
        deskripsi: slider.deskripsi || "",
        foto: null,
        link: slider.link || "",
        teks_tombol: slider.teks_tombol || "",
        judul_en: slider.judul_en || "",
        deskripsisingkat_en: slider.deskripsisingkat_en || "",
        deskripsi_en: slider.deskripsi_en || "",
        teks_tombol_en: slider.teks_tombol_en || "",
      });
      // Set image preview from existing foto
      if (slider.foto) {
        setImagePreview(getUploadUrl(slider.foto));
      } else {
        setImagePreview(null);
      }
    } else {
      setCurrentSlider(null);
      setFormData({
        judul: "",
        deskripsisingkat: "",
        deskripsi: "",
        foto: null,
        link: "",
        teks_tombol: "",
        judul_en: "",
        deskripsisingkat_en: "",
        deskripsi_en: "",
        teks_tombol_en: "",
      });
      setImagePreview(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentSlider(null);
    setFormData({
      link: "",
      teks_tombol: "",
      judul: "",
      deskripsisingkat: "",
      deskripsi: "",
      foto: null,
      judul_en: "",
      deskripsisingkat_en: "",
      deskripsi_en: "",
      teks_tombol_en: "",
    });
    setImagePreview(null);
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.size > 5 * 1024 * 1024 || !/\.(jpe?g|png|gif|webp)$/i.test(file.name))) {
      setError("Gunakan JPG, PNG, GIF, atau WebP dengan ukuran maksimal 5MB per gambar.");
      e.target.value = "";
      return;
    }
    if (file) {
      setFormData((prev) => ({ ...prev, foto: file }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("judul", formData.judul);
      formDataToSend.append("deskripsisingkat", formData.deskripsisingkat);
      formDataToSend.append("deskripsi", formData.deskripsi);
      formDataToSend.append("link", formData.link);
      formDataToSend.append("teks_tombol", formData.teks_tombol);
      formDataToSend.append("judul_en", formData.judul_en || "");
      formDataToSend.append("deskripsisingkat_en", formData.deskripsisingkat_en || "");
      formDataToSend.append("deskripsi_en", formData.deskripsi_en || "");
      formDataToSend.append("teks_tombol_en", formData.teks_tombol_en || "");

      if (formData.foto) {
        formDataToSend.append("foto", formData.foto);
      }
      if (mobileFile) formDataToSend.append("foto_mobile", mobileFile);
      if (removeMobile && !mobileFile) formDataToSend.append("remove_foto_mobile", "true");

      if (currentSlider) {
        await updateSlider(currentSlider.id, formDataToSend);
        alert("Slider berhasil diupdate!");
      } else {
        await createSlider(formDataToSend);
        alert("Slider berhasil ditambahkan!");
      }

      handleCloseModal();
      fetchSliders();
    } catch (err) {
      setError(
        "Gagal menyimpan slider: " +
          (err.response?.data?.message || err.message),
      );
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Apakah Anda yakin ingin menghapus slider ini?")) {
      try {
        await deleteSlider(id);
        alert("Slider berhasil dihapus!");
        fetchSliders();
      } catch (err) {
        setError(
          "Gagal menghapus slider: " +
            (err.response?.data?.message || err.message),
        );
        console.error(err);
      }
    }
  };

  return (
    <div className="slider-management">
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-images"></i>
            </div>
            <div>
              <h1 className="page-header-title">Kelola Slider</h1>
              <p className="page-header-subtitle">
                Manage slider gambar untuk halaman utama website
              </p>
            </div>
          </div>
          <Button
            variant="outline-light"
            onClick={() => handleShowModal()}
            className="add-button"
          >
            <i className="fas fa-plus me-2"></i>Tambah Slider
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="content-card mt-2">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : sliders.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-inbox fa-3x mb-3"></i>
              <p>Belum ada slider. Tambahkan slider pertama Anda!</p>
            </div>
          ) : (
            <Table responsive hover className="content-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Gambar</th>
                  <th>Judul</th>
                  <th>Deskripsi Singkat</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {sliders.map((slider) => (
                  <tr key={slider.id}>
                    <td>{slider.id}</td>
                    <td>
                      <div className="slider-thumbnail">
                        <img
                          src={
                            slider.foto
                              ? getUploadUrl(slider.foto)
                              : FALLBACK_SLIDER_IMAGE
                          }
                          alt={slider.judul}
                          onError={(e) => {
                            e.target.src = FALLBACK_SLIDER_IMAGE;
                          }}
                        />
                      </div>
                      <small className="text-muted">
                        {slider.foto_mobile ? "Desktop + mobile" : "Mobile memakai desktop"}
                      </small>
                    </td>
                    <td>
                      <strong>{slider.judul}</strong>
                    </td>
                    <td>
                      {slider.deskripsisingkat
                        ? slider.deskripsisingkat.length > 60
                          ? slider.deskripsisingkat.substring(0, 60) + "..."
                          : slider.deskripsisingkat
                        : "-"}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => handleShowModal(slider)}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(slider.id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal for Add/Edit Slider */}
      <Modal show={showModal} onHide={() => !submitting && handleCloseModal()} size="lg">
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>
            {currentSlider ? "Edit Slider" : "Tambah Slider Baru"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>
                Judul <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="judul"
                value={formData.judul}
                onChange={handleInputChange}
                placeholder="Masukkan judul slider"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Deskripsi Singkat</Form.Label>
              <Form.Control
                type="text"
                name="deskripsisingkat"
                maxLength={250}
                value={formData.deskripsisingkat}
                onChange={handleInputChange}
                placeholder="Deskripsi singkat untuk tampilan slider (maks 250 karakter)"
              />
              <Form.Text className="text-muted">
                Teks singkat yang muncul di bawah judul slider
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Deskripsi Lengkap</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="deskripsi"
                value={formData.deskripsi}
                onChange={handleInputChange}
                placeholder="Deskripsi lengkap (opsional, bisa berisi HTML)"
              />
              <Form.Text className="text-muted">
                Deskripsi detail, mendukung HTML untuk formatting
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Teks Tombol</Form.Label>
              <Form.Control
                type="text"
                name="teks_tombol"
                maxLength={100}
                value={formData.teks_tombol}
                onChange={handleInputChange}
                placeholder="Contoh: Daftar Sekarang, Selengkapnya, Lihat Detail"
              />
              <Form.Text className="text-muted">
                Teks yang muncul pada tombol di slider (opsional)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Link Tombol</Form.Label>
              <Form.Control
                type="text"
                name="link"
                maxLength={500}
                value={formData.link}
                onChange={handleInputChange}
                placeholder="Contoh: /pendaftaran-p3h, https://example.com"
              />
              <Form.Text className="text-muted">
                URL tujuan ketika tombol diklik. Bisa link internal (misal:
                /pendaftaran-p3h) atau eksternal (misal: https://google.com)
              </Form.Text>
            </Form.Group>

            {/* ---- English Version ---- */}
            <hr />
            <h6 className="text-muted mb-3">
              <i className="fas fa-language me-2"></i>Versi Bahasa Inggris (Opsional)
            </h6>

            <Form.Group className="mb-3">
              <Form.Label>Title (English)</Form.Label>
              <Form.Control
                type="text"
                name="judul_en"
                value={formData.judul_en}
                onChange={handleInputChange}
                placeholder="Enter slider title in English"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Short Description (English)</Form.Label>
              <Form.Control
                type="text"
                name="deskripsisingkat_en"
                maxLength={250}
                value={formData.deskripsisingkat_en}
                onChange={handleInputChange}
                placeholder="Short description in English"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Full Description (English)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="deskripsi_en"
                value={formData.deskripsi_en}
                onChange={handleInputChange}
                placeholder="Full description in English (HTML supported)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Button Text (English)</Form.Label>
              <Form.Control
                type="text"
                name="teks_tombol_en"
                maxLength={100}
                value={formData.teks_tombol_en}
                onChange={handleInputChange}
                placeholder="Example: Register Now, Learn More"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Banner Desktop{" "}
                {!currentSlider && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                onChange={handleImageChange}
                required={!currentSlider}
                disabled={submitting}
              />
              <Form.Text className="text-muted">
                Ukuran rekomendasi: 1920x600 px. Format: JPG, PNG, atau WebP.
                Maksimal 5MB.
              </Form.Text>
            </Form.Group>

            {imagePreview && (
              <div className="image-preview-container">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="image-preview"
                />
              </div>
            )}
            <Form.Group className="mb-3 mt-4" controlId="slider-mobile-image">
              <Form.Label>Banner Mobile (Opsional)</Form.Label>
              {!mobileSupported && (
                <Alert variant="info">
                  Upload mobile belum diaktifkan untuk tenant ini. Banner desktop tetap digunakan pada ponsel.
                </Alert>
              )}
              <Form.Control
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                disabled={!mobileSupported || submitting}
                onChange={(e) => {
                  const file = e.target.files[0];
                  e.target.value = "";
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024 || !/\.(jpe?g|png|gif|webp)$/i.test(file.name)) {
                    setError("Banner mobile harus JPG, PNG, GIF, atau WebP, maksimal 5MB.");
                    return;
                  }
                  setError(null);
                  setMobileFile(file);
                  setRemoveMobile(false);
                }}
              />
              <Form.Text className="text-muted">
                Rekomendasi: 1080 x 1350 px (4:5). Maksimal 5MB. Jika kosong, ponsel otomatis memakai banner desktop.
              </Form.Text>
              {mobilePreview && (
                <div className="mt-3">
                  <img src={mobilePreview} alt="Preview banner mobile"
                    style={{ width: "100%", maxWidth: 216, maxHeight: 300, objectFit: "contain", display: "block" }} />
                  <Button type="button" variant="outline-danger" size="sm" className="mt-2"
                    disabled={submitting || !mobileSupported}
                    onClick={() => { setMobileFile(null); setRemoveMobile(true); }}>
                    Hapus versi mobile, gunakan desktop
                  </Button>
                </div>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>
              Batal
            </Button>
            <Button variant="success" type="submit" disabled={submitting}>
              {submitting
                ? "Menyimpan..."
                : currentSlider
                  ? "Update"
                  : "Simpan"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default SliderManagement;
